import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import { message, Popover, Tooltip } from 'antd';
import { isAxiosError } from 'axios';
import { clsx } from 'clsx';
import dayjs from 'dayjs';
import {
  Calendar,
  ChevronDown,
  FoldVertical,
  Search,
  UnfoldVertical,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import z from 'zod';

import {
  GetLeadFeedParams,
  GetLeadFeedFilter,
  getGetLeadFeedQueryKey,
  useGetLead,
  useGetTwilioAccount,
  useListLeadFiles,
} from '../../../../api';
import { useCommunicationCapabilities, usePermissions } from '../../../../modules/account/hooks';
import { SmsForm } from '../../../../modules/communications/components/sms-form';
import { useOptionalCallContext } from '../../../../modules/communications/providers/call-context';
import { getStartCallErrorMessage } from '../../../../modules/communications/utils/start-call-error';
import { EmailForm } from '../../../../modules/connection/components/email-form';
import { MeetingForm } from '../../../../modules/connection/components/meeting-form';
import { LeadFeed } from '../../../../modules/feed/components/lead-feed';
import {
  LeadFeedComposeActions,
  type ComposerKind,
} from '../../../../modules/feed/components/lead-feed-compose-actions';
import {
  FEED_ACTIVITIES,
  LeadFeedViews,
  LeadFeedViewsMeasure,
  type FeedActivity,
} from '../../../../modules/feed/components/lead-feed-views';
import { LeadFiles } from '../../../../modules/lead/components/lead-files';
import { LeadFeedPanelToggle, LeadLayout } from '../../../../modules/lead/components/lead-layout';
import { LeadNoteForm } from '../../../../modules/lead/components/lead-note-form';
import { LeadPanels } from '../../../../modules/lead/components/lead-panels';
import { useLeadPanelSettings } from '../../../../modules/lead/hooks/useLeadPanelSettings';
import { usePageTitleDetail } from '../../../../modules/page-title';
import { DateRangePickerButton } from '../../../../ui/components/date-range-picker-button';
import { useDebouncedValue } from '../../../../ui/hooks';
import { getCallError } from '../../../../ui/utils/phone-number';

import './leadId.css';

import type { GetLeadResponseDtoContactsItem } from '../../../../api/generated/model';
import type { Mentionable } from '../../../../ui/components/mention';
import type { Dayjs } from 'dayjs';

type LeadRouteSearch = {
  activity?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  includeHidden?: boolean;
  hideSystemLogs?: boolean;
};

const normalizeActivity = (value?: string): FeedActivity => {
  if (value && FEED_ACTIVITIES.includes(value as FeedActivity)) {
    return value as FeedActivity;
  }
  return 'all';
};

const useObservedWidth = (onWidth: (width: number) => void) => {
  const onWidthRef = useRef(onWidth);
  onWidthRef.current = onWidth;
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameRef = useRef(0);

  return useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    cancelAnimationFrame(frameRef.current);
    if (!node) {
      return;
    }

    const style = getComputedStyle(node);
    onWidthRef.current(
      node.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
    );

    observerRef.current = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width == null) {
        return;
      }
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => onWidthRef.current(width));
    });
    observerRef.current.observe(node);
  }, []);
};

const LeadRoute = () => {
  const [availableWidth, setAvailableWidth] = useState(0);
  const [fullWidth, setFullWidth] = useState(0);
  const [viewsWidth, setViewsWidth] = useState(0);
  const feedHeadRef = useObservedWidth(setAvailableWidth);
  const feedHeadFullMeasureRef = useObservedWidth(setFullWidth);
  const feedHeadViewsMeasureRef = useObservedWidth(setViewsWidth);

  const showCompactHeader = availableWidth > 0 && fullWidth > 0 && availableWidth < fullWidth;
  const showCompactViews = availableWidth > 0 && viewsWidth > 0 && availableWidth < viewsWidth;

  const navigate = useNavigate({ from: '/leads/$leadId' });
  const queryClient = useQueryClient();
  const [allExpanded, setAllExpanded] = useState<boolean>();
  const [showSearchPopover, setShowSearchPopover] = useState(false);
  const searchPopoverInputRef = useRef<HTMLInputElement>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInitialToAddress, setEmailInitialToAddress] = useState<string | undefined>();
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [meetingInitialAttendeeEmail, setMeetingInitialAttendeeEmail] = useState<string | undefined>();
  const [showSmsForm, setShowSmsForm] = useState(false);
  const [smsInitialToNumber, setSmsInitialToNumber] = useState<string | undefined>();
  const [showNoteForm, setShowNoteForm] = useState(false);

  const { leadId } = Route.useParams();
  const { activity: activityParam = 'all', fromDate, toDate, search, hideSystemLogs } =
    Route.useSearch();
  const activity = normalizeActivity(activityParam);
  const [searchTerm, setSearchTerm] = useState(search ?? '');
  const [debouncedSearch] = useDebouncedValue(searchTerm);
  const { account, selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const viewerDisplayName = [account.firstName, account.lastName].filter(Boolean).join(' ').trim();
  const { can } = usePermissions();
  const canMakeCalls = can('calls', 'make');
  const callContext = useOptionalCallContext();

  const {
    canSendEmail,
    canAccessCalendar,
    canSendSms,
    connections,
    emailConnection,
    calendarConnection,
  } = useCommunicationCapabilities();

  const { data: twilioAccountData } = useGetTwilioAccount(selectedOrganization.id);
  const twilioAccount = twilioAccountData?.data.item;

  const invalidateFeed = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getGetLeadFeedQueryKey(selectedOrganization.id, leadId),
    });
  }, [queryClient, selectedOrganization.id, leadId]);

  const leadQuery = useGetLead(selectedOrganization.id, leadId);
  const lead = leadQuery.data?.data;
  usePageTitleDetail(lead?.name);

  // Company was deleted/archived (or never existed): GET returns 404, leaving the
  // page empty. Redirect back to the companies list instead.
  useEffect(() => {
    if (isAxiosError(leadQuery.error) && leadQuery.error.response?.status === 404) {
      navigate({ to: '/records' });
    }
  }, [leadQuery.error, navigate]);
  const recordCount = useMemo(
    () => 1 + (lead?.contacts?.length ?? 0) + (lead?.deals?.length ?? 0),
    [lead?.contacts?.length, lead?.deals?.length],
  );
  const mentionables = useMemo(
    (): Mentionable[] => [
      ...(lead?.owners ?? []).map((o): Mentionable => ({
        label: `${o.user.firstName} ${o.user.lastName}`,
        value: o.organizationMembershipId,
        kind: 'member',
      })),
      ...(lead?.contacts ?? []).map((c): Mentionable => ({
        label: `${c.firstName} ${c.lastName}`,
        value: c.id,
        kind: 'contact',
      })),
    ],
    [lead?.owners, lead?.contacts],
  );

  const contactPhoneMenuItems = useMemo(() => {
    const rows: { key: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const contact of lead?.contacts ?? []) {
      const name = `${contact.firstName} ${contact.lastName}`.trim();
      for (const phone of contact.phoneNumbers) {
        const value = phone.value.trim();
        if (!value || seen.has(value)) {
          continue;
        }
        seen.add(value);
        rows.push({
          key: value,
          label: `${name} · ${value}`,
        });
      }
    }
    return rows;
  }, [lead?.contacts]);

  const leadOwnerDisplayByEmail: Record<string, string> = {};
  for (const o of lead?.owners ?? []) {
    leadOwnerDisplayByEmail[o.user.email.toLowerCase()] = `${o.user.firstName} ${o.user.lastName}`;
  }

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      navigate({
        search: (prev: LeadRouteSearch) => ({
          ...prev,
          fromDate: undefined,
          toDate: undefined,
        }),
      });
      return;
    }
    const [start, end] = dates;
    navigate({
      search: (prev: LeadRouteSearch) => ({
        ...prev,
        fromDate: start.startOf('day').toISOString(),
        toDate: end.endOf('day').toISOString(),
      }),
    });
  };

  const calendarValue: [Dayjs | null, Dayjs | null] | null =
    fromDate && toDate ? [dayjs(fromDate), dayjs(toDate)] : null;

  const { data: filesResponse } = useListLeadFiles(selectedOrganization.id, leadId);
  const fileItems = filesResponse?.data?.items;

  const { settings: panelSettings, setPanelTitle } = useLeadPanelSettings(
    selectedOrganization.id,
    selectedMembership.id,
  );
  const filesTitle = panelSettings.filesTitle ?? 'All Files';
  const recordsTitle = panelSettings.recordsTitle ?? 'All Records';

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      document.querySelector('.lead-layout-content')?.scrollTo({ top: 1e9, behavior: 'smooth' });
    }, 100);
  }, []);

  const closeAllComposers = useCallback(() => {
    setShowEmailForm(false);
    setShowSmsForm(false);
    setShowMeetingForm(false);
    setShowNoteForm(false);
    setEmailInitialToAddress(undefined);
    setSmsInitialToNumber(undefined);
    setMeetingInitialAttendeeEmail(undefined);
  }, []);

  const openActivityEmailCompose = useCallback(
    (toAddress?: string) => {
      setEmailInitialToAddress(toAddress);
      setShowEmailForm(true);
      setShowSmsForm(false);
      setShowMeetingForm(false);
      setShowNoteForm(false);
      scrollToBottom();
    },
    [scrollToBottom],
  );

  const handleComposeCall = useCallback((contact?: GetLeadResponseDtoContactsItem) => {
    if (!canMakeCalls || !twilioAccount || !callContext || contactPhoneMenuItems.length === 0) {
      message.error('Could not start call');
      return;
    }
    if (contact && !contact.phoneNumbers.find((phone) => phone.isPrimary)?.value) {
      message.error('This contact has no phone number');
      return;
    }
    const phone =
      contact?.phoneNumbers.find((phone) => phone.isPrimary)?.value ??
      (contactPhoneMenuItems.length === 1 ? contactPhoneMenuItems[0]!.key : undefined);
    if (!phone) {
      message.error('Please select a contact to call');
      return;
    }
    const callError = getCallError(phone);
    if (callError) {
      message.error(callError);
      return;
    }
    const currentOwner = lead?.owners?.find(
      (o) => o.organizationMembershipId === selectedMembership.id,
    );
    callContext
      .startCall(phone, {
        leadName: lead?.name || undefined,
        leadOwnerName: currentOwner
          ? `${currentOwner.user.firstName} ${currentOwner.user.lastName}`
          : undefined,
      })
      .catch((error: unknown) => message.error(getStartCallErrorMessage(error)));
  }, [
    callContext,
    canMakeCalls,
    contactPhoneMenuItems,
    lead?.name,
    lead?.owners,
    selectedMembership.id,
    twilioAccount,
  ]);

  const toggleComposer = useCallback(
    (kind: ComposerKind, contact?: GetLeadResponseDtoContactsItem) => {
      const isOpen =
        (kind === 'email' && showEmailForm) ||
        (kind === 'sms' && showSmsForm) ||
        (kind === 'meeting' && showMeetingForm) ||
        (kind === 'note' && showNoteForm);

      if (isOpen && !contact) {
        closeAllComposers();
        return;
      }

      closeAllComposers();
      switch (kind) {
        case 'email':
          openActivityEmailCompose(contact?.emails.find((email) => email.isPrimary)?.value);
          break;
        case 'sms':
          setSmsInitialToNumber(contact?.phoneNumbers.find((phone) => phone.isPrimary)?.value);
          setShowSmsForm(true);
          scrollToBottom();
          break;
        case 'meeting':
          setMeetingInitialAttendeeEmail(contact?.emails.find((email) => email.isPrimary)?.value);
          setShowMeetingForm(true);
          scrollToBottom();
          break;
        case 'note':
          setShowNoteForm(true);
          scrollToBottom();
          break;
        case 'call':
          break;
        default:
          break;
      }
    },
    [
      closeAllComposers,
      openActivityEmailCompose,
      scrollToBottom,
      showEmailForm,
      showMeetingForm,
      showNoteForm,
      showSmsForm,
    ],
  );

  const trimmedDebouncedSearch = debouncedSearch.trim();
  const effectiveSearch = trimmedDebouncedSearch.length >= 3 ? trimmedDebouncedSearch : undefined;
  const isSystemLogsHidden = hideSystemLogs === true;

  const activeComposer: ComposerKind | null = showEmailForm
    ? 'email'
    : showSmsForm
      ? 'sms'
      : showMeetingForm
        ? 'meeting'
        : showNoteForm
          ? 'note'
          : null;

  const handleClearActivitySearch = useCallback(() => {
    setSearchTerm('');
    navigate({
      search: (prev: LeadRouteSearch) => ({ ...prev, search: undefined }),
      replace: true,
    });
  }, [navigate]);

  useEffect(() => {
    const nextSearch = effectiveSearch ?? '';
    const currentSearch = search?.trim() ?? '';
    if (nextSearch === currentSearch) {
      return;
    }
    navigate({
      search: (prev: LeadRouteSearch) => ({
        ...prev,
        search: effectiveSearch,
      }),
      replace: true,
    });
  }, [effectiveSearch, navigate, search]);

  useEffect(() => {
    if (!showCompactHeader) {
      setShowSearchPopover(false);
    }
  }, [showCompactHeader]);

  const canComposeCall = canMakeCalls && !!twilioAccount && contactPhoneMenuItems.length > 0;

  const activitySearchField = (inputRef?: RefObject<HTMLInputElement | null>) => (
    <div className="feed-search">
      <Search size={12} />
      <input
        ref={inputRef}
        data-testid="feed-search"
        className="feed-search-input"
        placeholder="Search activity…"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
      />
      {searchTerm ? (
        <Tooltip title="Clear search">
          <button
            type="button"
            className="feed-search-clear"
            aria-label="Clear search"
            onClick={handleClearActivitySearch}
          >
            <X size={11} />
          </button>
        </Tooltip>
      ) : null}
    </div>
  );

  const headMeasureRow = (secondaryCompact: boolean) => (
    <>
      <span className="icon-pill" />
      <LeadFeedViewsMeasure value={activity} />
      <div className="lead-feed-head-measure-right">
        {secondaryCompact ? (
          <span className="icon-pill" />
        ) : (
          <span className="feed-search">
            <Search size={12} />
            <span className="feed-search-input">Search activity…</span>
          </span>
        )}
        <span className="simple-bar-sep" />
        <span className="icon-pill" />
        {secondaryCompact ? (
          <span className="icon-pill" />
        ) : (
          <span className="chip-btn">
            <Calendar size={12} />
            Date
            <ChevronDown size={11} />
          </span>
        )}
        <span className="icon-pill" />
      </div>
    </>
  );

  return (
    <LeadLayout
      userId={account.id}
      leftResizerHeader={filesTitle}
      leftFileCount={fileItems?.length ?? 0}
      onRenameLeftTitle={(value) => setPanelTitle('filesTitle', value)}
      left={<LeadFiles organizationId={selectedOrganization.id} leadId={leadId} />}
      right={
        <LeadPanels
          organizationId={selectedOrganization.id}
          leadId={leadId}
          onRequestEmailCompose={openActivityEmailCompose}
        />
      }
      rightResizerHeader={recordsTitle}
      rightRecordCount={recordCount}
      onRenameRightTitle={(value) => setPanelTitle('recordsTitle', value)}
      headerLeft={
        <div ref={feedHeadRef} className="lead-feed-head">
          <div ref={feedHeadFullMeasureRef} className="lead-feed-head-measure" aria-hidden="true">
            {headMeasureRow(false)}
          </div>
          <div ref={feedHeadViewsMeasureRef} className="lead-feed-head-measure" aria-hidden="true">
            {headMeasureRow(true)}
          </div>
          <LeadFeedPanelToggle side="left" title={filesTitle} />
          <LeadFeedViews
            compact={showCompactViews}
            value={activity}
            onChange={(viewId) => {
              navigate({
                search: (prev: LeadRouteSearch) => ({
                  ...prev,
                  activity: viewId === 'all' ? undefined : viewId,
                }),
              });
            }}
          />

          <div className="simple-bar-right">
            {showCompactHeader ? (
              <Tooltip title="Search activity">
                <Popover
                  open={showSearchPopover}
                  onOpenChange={(open) => {
                    setShowSearchPopover(open);
                    if (open) {
                      requestAnimationFrame(() => searchPopoverInputRef.current?.focus());
                    }
                  }}
                  trigger="click"
                  placement="bottomRight"
                  overlayClassName="feed-search-popover"
                  content={activitySearchField(searchPopoverInputRef)}
                >
                  <button
                    type="button"
                    className={clsx(
                      'icon-pill',
                      (showSearchPopover || searchTerm) && 'active',
                    )}
                    aria-label="Search activity"
                    data-testid="feed-search-open"
                  >
                    <Search size={16} />
                  </button>
                </Popover>
              </Tooltip>
            ) : (
              activitySearchField()
            )}

            <span className="simple-bar-sep" />

            <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
              <button
                type="button"
                data-testid="feed-expand-all"
                className={clsx('icon-pill', allExpanded && 'active')}
                onClick={() => setAllExpanded((prev) => !prev)}
              >
                {allExpanded ? <FoldVertical size={16} /> : <UnfoldVertical size={16} />}
              </button>
            </Tooltip>

            <DateRangePickerButton
              value={calendarValue}
              onChange={handleDateRangeChange}
              allowClear
              variant={showCompactHeader ? 'icon' : 'chip'}
            />

            <LeadFeedPanelToggle side="right" title={recordsTitle} />
          </div>
        </div>
      }
    >
      <LeadFeed
        organizationId={selectedOrganization.id}
        organizationMembershipId={selectedMembership.id}
        viewerDisplayName={viewerDisplayName || undefined}
        leadId={leadId}
        smsContactsDirectory={lead?.contacts}
        emailContactsDirectory={lead?.contacts}
        leadOwnerDisplayByEmail={leadOwnerDisplayByEmail}
        queryParams={
          {
            filter: activity as GetLeadFeedFilter,
            fromDate,
            toDate,
            search: effectiveSearch,
          } as GetLeadFeedParams
        }
        hideSystemLogs={isSystemLogsHidden}
        forceExpanded={allExpanded}
        calendarConnectionId={calendarConnection?.id}
        mentionables={mentionables}
        onEditSuccess={invalidateFeed}
        onClearSearch={handleClearActivitySearch}
        composeActions={
          !activeComposer && (
            <LeadFeedComposeActions
              contacts={lead?.contacts ?? []}
              canSendEmail={canSendEmail}
              canSendSms={canSendSms}
              canComposeCall={canComposeCall}
              canAccessCalendar={canAccessCalendar}
              onToggleComposer={toggleComposer}
              onComposeCall={handleComposeCall}
            />
          )
        }
      >
        {showEmailForm && emailConnection ? (
          <EmailForm
            key={emailInitialToAddress ?? ''}
            organizationId={selectedOrganization.id}
            connectionId={emailConnection.id}
            emailConnections={connections}
            contacts={lead?.contacts ?? []}
            senderName={viewerDisplayName}
            initialValues={emailInitialToAddress ? { toAddresses: [emailInitialToAddress] } : {}}
            mentionables={mentionables}
            onSuccess={() => {
              closeAllComposers();
              invalidateFeed();
            }}
            onCancel={closeAllComposers}
          />
        ) : null}
        {showMeetingForm && canAccessCalendar ? (
          <MeetingForm
            key={meetingInitialAttendeeEmail ?? 'meeting'}
            organizationId={selectedOrganization.id}
            connections={connections}
            contacts={lead?.contacts ?? []}
            senderName={viewerDisplayName}
            initialValues={meetingInitialAttendeeEmail ? { attendeeEmails: [meetingInitialAttendeeEmail] } : undefined}
            onSuccess={() => {
              closeAllComposers();
              invalidateFeed();
            }}
            onCancel={closeAllComposers}
          />
        ) : null}
        {showSmsForm ? (
          <SmsForm
            key={smsInitialToNumber ?? 'sms'}
            organizationId={selectedOrganization.id}
            organizationMembershipId={selectedMembership.id}
            contacts={lead?.contacts}
            initialToNumber={smsInitialToNumber}
            mentionables={mentionables}
            onSuccess={() => {
              closeAllComposers();
              invalidateFeed();
            }}
            onCancel={closeAllComposers}
          />
        ) : null}
        {showNoteForm ? (
          <LeadNoteForm
            organizationId={selectedOrganization.id}
            leadId={leadId}
            mentionables={mentionables}
            onSuccess={() => {
              closeAllComposers();
              invalidateFeed();
            }}
            onCancel={closeAllComposers}
          />
        ) : null}
      </LeadFeed>
    </LeadLayout>
  );
};

const LeadRouteSearchParams = z.object({
  activity: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  search: z.string().optional(),
  includeHidden: z.boolean().optional(),
  hideSystemLogs: z.boolean().optional(),
});

export const Route = createFileRoute('/_authenticated/_app/leads/$leadId')({
  component: LeadRoute,
  validateSearch: (params: unknown) => LeadRouteSearchParams.parse(params),
});
